import type { Column } from '@/components/Organisms/DataTable/DataTable.types';
import { colors } from '@/components/ui/theme';

/**
 * Consignment Order Status code mapping with colors for the unified view
 */
const STATUS_MAP: Record<number, { label: string; color: string }> = {
	953870000: { label: 'Pending Shipment', color: colors.pendingYellow }, // Open
	953870001: { label: 'Shipped', color: colors.success }, // closed
	953870002: { label: 'Order Denied', color: colors.deniedRed }, // Order Denied
	953870003: { label: 'Pending Shipment', color: colors.pendingYellow }, // Ready To Ship
	953870004: { label: 'Pending Shipment', color: colors.pendingYellow }, // Sent to FB
	953870005: { label: 'Partial Shipment', color: colors.statusTeal },
	953870006: { label: 'Pending Shipment', color: colors.pendingYellow }, // Submitted to Mfr
};

/**
 * Maps Consignment Order status code to status object with label and color
 * @param statusCode - The status code from data
 * @returns Object with label and color, or default values
 */
export const getConsignmentOrderStatus = (
	statusCode: number | null | undefined,
): { label: string; color: string } => {
	// Handle null or undefined status as "Incomplete"
	if (statusCode === null || statusCode === undefined) {
		return { label: 'Incomplete', color: colors.defaultGray };
	}
	return STATUS_MAP[statusCode] || { label: '-', color: colors.defaultGray };
};

/**
 * Consignment Type mapping
 */
const CONSIGNMENT_TYPE_MAP: Record<number, string> = {
	953870001: 'Trunk Stock',
};

/**
 * Maps Consignment type code to type name
 * @param typeCode - The type code from data
 * @returns Type name string
 */
export const getConsignmentTypeName = (typeCode?: number): string => {
	if (!typeCode) return '-';
	return CONSIGNMENT_TYPE_MAP[typeCode] || '-';
};

// Status filter options for dropdown (replaces tabs)
export const statusFilterOptions = [
	{ label: 'All', value: '' },
	{ label: 'Pending Shipment', value: 'pending' }, // Sent to FB
	{ label: 'Partial Shipment', value: 953870005 },
	{ label: 'Shipped', value: 953870001 },
	{ label: 'Order Denied', value: 953870002 }, // Order Denied
	{ label: 'Incomplete', value: 'incomplete' }, // NULL status
];

// Unified columns for all consignment orders
export const columns: Column[] = [
	{
		key: 'consignmentType',
		label: 'Consignment Type',
		width: '140px',
		type: 'string',
		sorter: false,
		hideColumnFilter: true,
	},
	{
		key: 'ta_Clinic.name',
		label: 'Clinic',
		width: '160px',
		type: 'string',
		sorter: true,
	},
	{
		key: 'ta_clinicpo',
		label: 'Clinic PO',
		width: '120px',
		type: 'string',
		sorter: true,
	},
	{
		key: 'ta_Doctor.fullname',
		label: 'Provider',
		width: '150px',
		type: 'string',
		sorter: true,
	},
	{
		key: 'ta_BVPatient.fullname',
		label: 'Patient',
		width: '150px',
		type: 'string',
		sorter: true,
	},
	{
		key: 'ta_SKU.ta_name',
		label: 'Product',
		width: '150px',
		type: 'string',
		sorter: true,
	},
	{
		key: 'order.ta_quantity',
		label: 'Qty',
		width: '70px',
		type: 'number',
		sorter: true,
	},
	{
		key: 'ta_orderrequestdate',
		label: 'Request Date',
		width: '100px',
		type: 'date',
		dateISO: true,
		sorter: true,
	},
	{
		key: 'ta_dos',
		label: 'DOS',
		width: '100px',
		type: 'date',
		dateISO: true,
		sorter: true,
	},
	{
		key: 'ta_ShippingLocation.ta_addressline',
		label: 'Shipping Location',
		width: '180px',
		type: 'string',
		sorter: true,
	},
	{
		key: 'ta_status',
		label: 'Status',
		width: '150px',
		type: 'string-status',
		sorter: false,
		filterOptions: statusFilterOptions,
	},
];
